import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiErrorDetail } from "../../lib/api";
import { toast, Toaster } from "sonner";
import {
  ArrowRight,
  ArrowLeft,
  Check,
  CheckCircle2,
  Loader2,
  Sparkles,
  ShieldCheck,
  AlertTriangle,
} from "lucide-react";

const STEPS = [
  { key: "profile", label: "Merchant profile" },
  { key: "kfs", label: "KFS" },
  { key: "cover", label: "Cover options" },
  { key: "payment", label: "Payment" },
  { key: "dashboard", label: "Dashboard" },
];

function ProgressIndicator({ active }) {
  const activeIdx = STEPS.findIndex((s) => s.key === active);
  return (
    <ol
      className="flex items-center gap-2 sm:gap-3 overflow-x-auto pb-1"
      data-testid="cover-progress"
    >
      {STEPS.map((s, i) => {
        const done = i < activeIdx;
        const current = i === activeIdx;
        return (
          <li key={s.key} className="flex items-center gap-2 sm:gap-3 shrink-0">
            <span
              className={`w-7 h-7 rounded-full grid place-items-center text-xs font-bold transition-colors ${
                done
                  ? "bg-[#2d7a4a] text-white"
                  : current
                    ? "bg-[#114232] text-[#f4f2eb]"
                    : "bg-[#114232]/8 text-[#114232]"
              }`}
            >
              {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : i + 1}
            </span>
            <span
              className={`text-xs sm:text-sm font-semibold whitespace-nowrap ${
                current ? "text-[#114232]" : done ? "text-[#5c6360]" : "text-[#5c6360]/70"
              }`}
            >
              {s.label}
            </span>
            {i < STEPS.length - 1 && <span className="w-4 sm:w-8 h-px bg-[#114232]/15" />}
          </li>
        );
      })}
    </ol>
  );
}

function formatKES(value) {
  return new Intl.NumberFormat("en-KE", {
    style: "currency",
    currency: "KES",
    maximumFractionDigits: 0,
  }).format(value);
}

export default function CoverOptions() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selecting, setSelecting] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const r = await api.get("/merchant/cover-options");
      setData(r.data);
    } catch (e) {
      const detail = formatApiErrorDetail(e.response?.data?.detail) || e.message;
      setError(detail);
      if (e.response?.status === 401) {
        navigate("/login", { replace: true });
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    load();
  }, [load]);

  async function pickTier(code) {
    if (!data?.kfs_acknowledged) {
      toast.error("Please accept the KFS first.");
      navigate("/merchant/kfs");
      return;
    }
    setSelecting(code);
    try {
      const r = await api.post("/merchant/cover-options/quote", { tier: code });
      toast.success(r.data.reused ? "Quote updated" : "Quote ready");
      navigate(`/merchant/payment?policy_id=${r.data.policy.id}`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    } finally {
      setSelecting("");
    }
  }

  return (
    <div
      className="px-5 sm:px-8 py-6 sm:py-8 space-y-6 sm:space-y-8 max-w-5xl mx-auto"
      data-testid="cover-options-page"
    >
      <Toaster richColors position="top-right" />

      <div>
        <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#c15337]">
          Cover options
        </div>
        <h1 className="font-heading text-3xl sm:text-4xl font-extrabold text-[#114232] mt-1.5 flex items-center gap-3">
          <ShieldCheck className="w-7 h-7 sm:w-8 sm:h-8" />
          Choose your cover tier
        </h1>
        <p className="text-sm sm:text-base text-[#5c6360] mt-2 max-w-2xl leading-relaxed">
          Weekly premium, paid via M-Pesa. Pause or upgrade anytime — seven-day cooling-off on
          first activation.
        </p>
      </div>

      <ProgressIndicator active="cover" />

      {!data?.kfs_acknowledged && !loading && !error && (
        <div
          className="rounded-2xl bg-[#d49a36]/8 border border-[#d49a36]/30 p-5 flex items-start justify-between gap-4"
          data-testid="cover-kfs-warning"
        >
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-[#8a6520] shrink-0 mt-0.5" strokeWidth={2} />
            <div>
              <div className="font-heading font-bold text-[#114232]">KFS not yet accepted</div>
              <div className="text-sm text-[#5c6360] mt-0.5">
                You'll need to accept the Key Facts Statement before picking a tier.
              </div>
            </div>
          </div>
          <Link
            to="/merchant/kfs"
            data-testid="cover-go-to-kfs"
            className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] text-xs font-semibold transition-colors shrink-0"
          >
            Go to KFS <ArrowRight className="w-3.5 h-3.5" />
          </Link>
        </div>
      )}

      {loading ? (
        <div
          className="rounded-2xl bg-white border border-[#114232]/10 p-12 grid place-items-center text-[#5c6360]"
          data-testid="cover-loading"
        >
          <Loader2 className="w-5 h-5 animate-spin" />
        </div>
      ) : error ? (
        <div className="rounded-2xl bg-[#c15337]/8 border border-[#c15337]/25 p-5" data-testid="cover-error">
          <strong>Couldn't load cover options.</strong>{" "}
          <span className="text-[#5c6360]">{error}</span>
          <button
            onClick={load}
            data-testid="cover-retry"
            className="ml-3 inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-[#114232]/20 rounded-full bg-white hover:bg-[#114232]/5 text-[#114232] transition-colors"
          >
            Retry
          </button>
        </div>
      ) : (
        <div className="grid lg:grid-cols-3 gap-5 lg:gap-6 pt-2">
          {data.tiers.map((t) => {
            const isCurrent = data.current_policy?.tier === t.code;
            return (
              <div
                key={t.code}
                data-testid={`tier-card-${t.code}`}
                className={`relative rounded-3xl p-7 lg:p-8 flex flex-col transition-all hover:shadow-xl ${
                  t.featured
                    ? "bg-[#114232] text-[#f4f2eb] shadow-lg lg:-translate-y-4"
                    : "bg-white border border-[#114232]/10 hover:border-[#114232]/30"
                }`}
              >
                {t.featured && (
                  <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-[#c15337] text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                    <Sparkles className="w-3 h-3" /> Most popular
                  </div>
                )}
                {isCurrent && (
                  <div className="absolute -top-3 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-[#2d7a4a] text-white text-[10px] font-bold uppercase tracking-wider shadow-md">
                    Current
                  </div>
                )}
                <div
                  className={`text-[10px] uppercase tracking-[0.22em] font-bold mb-2 ${
                    t.featured ? "text-[#d49a36]" : "text-[#c15337]"
                  }`}
                >
                  {t.name}
                </div>
                <div className="flex items-baseline gap-1 mb-2">
                  <span className="font-heading text-4xl sm:text-5xl font-extrabold metric-value">
                    {formatKES(t.premium_weekly_kes)}
                  </span>
                  <span className={`text-sm ${t.featured ? "text-[#f4f2eb]/70" : "text-[#5c6360]"}`}>
                    /week
                  </span>
                </div>
                <div className={`text-xs mb-1 ${t.featured ? "text-[#f4f2eb]/75" : "text-[#5c6360]"}`}>
                  Cover up to {formatKES(t.cover_limit_kes)} per claim
                </div>
                <div className={`text-xs mb-5 italic ${t.featured ? "text-[#f4f2eb]/65" : "text-[#5c6360]"}`}>
                  {t.best_for}
                </div>
                <ul className="space-y-2.5 mb-7 flex-1">
                  {t.perks.map((p) => (
                    <li key={p} className="flex items-start gap-2.5 text-sm">
                      <Check
                        className={`w-4 h-4 mt-0.5 shrink-0 ${
                          t.featured ? "text-[#d49a36]" : "text-[#2d7a4a]"
                        }`}
                        strokeWidth={3}
                      />
                      <span className={t.featured ? "text-[#f4f2eb]/95" : "text-[#1b1d1c]"}>{p}</span>
                    </li>
                  ))}
                </ul>
                <button
                  onClick={() => pickTier(t.code)}
                  disabled={selecting !== "" || !data.kfs_acknowledged}
                  data-testid={`tier-pick-${t.code}`}
                  className={`inline-flex items-center justify-center gap-2 px-5 py-3 rounded-full font-semibold transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                    t.featured
                      ? "bg-[#c15337] hover:bg-[#a8472f] text-white shadow-md hover:shadow-lg"
                      : "bg-[#114232] hover:bg-[#0d3125] text-[#f4f2eb] shadow-sm hover:shadow-md"
                  }`}
                >
                  {selecting === t.code ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" /> Preparing…
                    </>
                  ) : isCurrent ? (
                    <>
                      Continue with {t.name} <ArrowRight className="w-4 h-4" />
                    </>
                  ) : (
                    <>
                      Select {t.name} <ArrowRight className="w-4 h-4" />
                    </>
                  )}
                </button>
              </div>
            );
          })}
        </div>
      )}

      <div className="flex items-center gap-3 pt-2">
        <Link
          to="/merchant/kfs"
          data-testid="cover-back-kfs"
          className="inline-flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm font-semibold text-[#5c6360] hover:text-[#114232] transition-colors"
        >
          <ArrowLeft className="w-4 h-4" /> Back to KFS
        </Link>
      </div>
    </div>
  );
}
